/**
 * m3 echo Frontend - Grounding Benchmark Tab
 *
 * Tools tab for comparing LLM responses with and without web search grounding.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import {
  groundingBenchmarkApi,
  GroundingBenchmarkRun,
  GroundingBenchmarkRunDetail,
  GroundingBenchmarkResult,
  GroundingBenchmarkStatus,
  GroundingBenchmarkPrompt,
} from '../../services/admin';
import GroundingBenchmarkDetailDialog from './GroundingBenchmarkDetailDialog';

// ===========================
// Helper Functions
// ===========================

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'pending':
      return 'default';
    case 'running':
      return 'primary';
    case 'completed':
      return 'success';
    case 'partially_completed':
      return 'warning';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Ausstehend';
    case 'running':
      return 'Läuft';
    case 'completed':
      return 'Abgeschlossen';
    case 'partially_completed':
      return 'Teilweise abgeschlossen';
    case 'failed':
      return 'Fehlgeschlagen';
    case 'cancelled':
      return 'Abgebrochen';
    default:
      return status;
  }
};

const getResultStatusColor = (status: string): 'default' | 'success' | 'error' => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

// ===========================
// Component
// ===========================

export const GroundingBenchmarkTab: React.FC = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Available models
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);

  // Available prompts
  const [availablePrompts, setAvailablePrompts] = useState<GroundingBenchmarkPrompt[]>([]);
  const [selectedPrompts, setSelectedPrompts] = useState<GroundingBenchmarkPrompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Runs
  const [runs, setRuns] = useState<GroundingBenchmarkRun[]>([]);
  const [activeRun, setActiveRun] = useState<GroundingBenchmarkRunDetail | null>(null);
  const [runStatus, setRunStatus] = useState<GroundingBenchmarkStatus | null>(null);

  // Dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<GroundingBenchmarkResult | null>(null);

  // Actions
  const [cancelling, setCancelling] = useState(false);
  const [starting, setStarting] = useState(false);

  // Polling ref
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // ===========================
  // Data Loading
  // ===========================

  const loadAvailableModels = useCallback(async () => {
    try {
      const response = await groundingBenchmarkApi.getAvailableModels();
      setAvailableModels(response.data.models);
    } catch (err: any) {
      console.error('Error loading models:', err);
    }
  }, []);

  const loadPrompts = useCallback(async () => {
    setPromptsLoading(true);
    try {
      // Load prompts from dedicated grounding benchmark endpoint
      const response = await groundingBenchmarkApi.getPrompts();
      setAvailablePrompts(response.data.prompts);
    } catch (err: any) {
      console.error('Error loading prompts:', err);
    } finally {
      setPromptsLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await groundingBenchmarkApi.getRuns();
      setRuns(response.data);

      // Check for active run
      const active = response.data.find(r => r.status === 'running' || r.status === 'pending');
      if (active) {
        const resultsResponse = await groundingBenchmarkApi.getResults(active.id);
        setActiveRun(resultsResponse.data);
      } else {
        setActiveRun(null);
      }

      setError(null);
    } catch (err: any) {
      console.error('Error loading runs:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Benchmark-Läufe');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    if (!activeRun) return;

    try {
      const response = await groundingBenchmarkApi.getStatus(activeRun.id);
      setRunStatus(response.data);

      // If completed, reload full results
      if (response.data.status !== 'running' && response.data.status !== 'pending') {
        await loadRuns();
      }
    } catch (err) {
      console.error('Error polling status:', err);
    }
  }, [activeRun, loadRuns]);

  // ===========================
  // Effects
  // ===========================

  // Initial load
  useEffect(() => {
    loadAvailableModels();
    loadPrompts();
    loadRuns();
  }, [loadAvailableModels, loadPrompts, loadRuns]);

  // Polling for active run
  useEffect(() => {
    if (activeRun && (activeRun.status === 'running' || activeRun.status === 'pending')) {
      pollingRef.current = setInterval(pollStatus, 2000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeRun, pollStatus]);

  // ===========================
  // Handlers
  // ===========================

  const handleModelToggle = (model: string) => {
    setSelectedModels(prev =>
      prev.includes(model)
        ? prev.filter(m => m !== model)
        : [...prev, model]
    );
  };

  const handleStartBenchmark = async () => {
    if (selectedModels.length === 0) {
      setError('Bitte wählen Sie mindestens ein Modell aus');
      return;
    }
    if (selectedPrompts.length === 0) {
      setError('Bitte wählen Sie mindestens einen Prompt aus');
      return;
    }

    setStarting(true);
    setError(null);

    try {
      await groundingBenchmarkApi.start({
        models: selectedModels,
        prompt_ids: selectedPrompts.map(p => p.id),
      });

      setSuccess('Benchmark gestartet');
      setTimeout(() => setSuccess(null), 3000);

      // Reload to get the active run
      await loadRuns();

      // Clear selections
      setSelectedModels([]);
      setSelectedPrompts([]);
    } catch (err: any) {
      console.error('Error starting benchmark:', err);
      setError(err.response?.data?.detail || 'Fehler beim Starten des Benchmarks');
    } finally {
      setStarting(false);
    }
  };

  const handleCancelBenchmark = async () => {
    if (!activeRun) return;

    setCancelling(true);
    try {
      await groundingBenchmarkApi.cancel(activeRun.id);
      setSuccess('Benchmark wird abgebrochen');
      setTimeout(() => setSuccess(null), 3000);
      await loadRuns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Abbrechen');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteRun = async (runId: number) => {
    try {
      await groundingBenchmarkApi.delete(runId);
      setSuccess('Benchmark gelöscht');
      setTimeout(() => setSuccess(null), 3000);
      await loadRuns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleViewResult = (result: GroundingBenchmarkResult) => {
    setSelectedResult(result);
    setDetailDialogOpen(true);
  };

  const handleViewRun = async (run: GroundingBenchmarkRun) => {
    try {
      const response = await groundingBenchmarkApi.getResults(run.id);
      setActiveRun(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Ergebnisse');
    }
  };

  // ===========================
  // Render
  // ===========================

  const isRunning = activeRun && (activeRun.status === 'running' || activeRun.status === 'pending');
  const progressPercent = runStatus?.progress_percent || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Grounding Benchmark</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadRuns}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Configuration Section */}
      {!isRunning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Neuer Benchmark
            </Typography>

            <Grid container spacing={3}>
              {/* Model Selection */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Modelle auswählen:
                </Typography>
                <FormGroup row>
                  {availableModels.map(model => (
                    <FormControlLabel
                      key={model}
                      control={
                        <Checkbox
                          checked={selectedModels.includes(model)}
                          onChange={() => handleModelToggle(model)}
                        />
                      }
                      label={model}
                    />
                  ))}
                </FormGroup>
              </Grid>

              {/* Prompt Selection */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Prompts auswählen:
                </Typography>
                <Autocomplete
                  multiple
                  options={availablePrompts}
                  loading={promptsLoading}
                  value={selectedPrompts}
                  onChange={(_, newValue) => setSelectedPrompts(newValue)}
                  getOptionLabel={(option) => `${option.project_name}: ${option.text}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Prompts auswählen..."
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.text}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.project_name}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              {/* Start Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={starting ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  onClick={handleStartBenchmark}
                  disabled={starting || selectedModels.length === 0 || selectedPrompts.length === 0}
                >
                  {starting ? 'Wird gestartet...' : 'Benchmark starten'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {selectedPrompts.length} Prompts × {selectedModels.length} Modelle = {selectedPrompts.length * selectedModels.length} Tests
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Progress Section */}
      {isRunning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Benchmark läuft...
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
                onClick={handleCancelBenchmark}
                disabled={cancelling}
              >
                Abbrechen
              </Button>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ mb: 1, height: 10, borderRadius: 1, '& .MuiLinearProgress-bar': { borderRadius: 1, background: progressPercent >= 100 ? 'linear-gradient(90deg, #4caf50, #81c784)' : 'linear-gradient(90deg, #678BA3, #8AA4B8)' } }}
            />

            <Typography variant="body2" color="text.secondary">
              {runStatus?.completed_tests || activeRun.completed_tests} / {activeRun.total_tests} Tests abgeschlossen
              ({Math.round(progressPercent)}%)
              {runStatus?.failed_tests ? ` - ${runStatus.failed_tests} fehlgeschlagen` : ''}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results Table (active run) */}
      {activeRun && activeRun.results && activeRun.results.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Ergebnisse (Run #{activeRun.id})
              </Typography>
              <Chip
                label={getStatusLabel(activeRun.status)}
                color={getStatusColor(activeRun.status)}
                size="small"
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Prompt</TableCell>
                    <TableCell>Modell</TableCell>
                    <TableCell align="center">Ohne Grounding</TableCell>
                    <TableCell align="center">Mit Grounding</TableCell>
                    <TableCell align="center">Quellen</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRun.results.map((result) => (
                    <TableRow key={result.id} hover>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={result.prompt_text}>
                          <span>{result.prompt_text.substring(0, 50)}...</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{result.model_name}</TableCell>
                      <TableCell align="center">
                        {result.latency_no_grounding_ms
                          ? `${(result.latency_no_grounding_ms / 1000).toFixed(1)}s`
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {result.latency_with_grounding_ms
                          ? `${(result.latency_with_grounding_ms / 1000).toFixed(1)}s`
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {result.sources ? (
                          <Chip
                            icon={<LinkIcon />}
                            label={result.sources.length}
                            size="small"
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={result.status === 'success' ? 'OK' : result.status === 'failed' ? 'Fehler' : 'Pending'}
                          color={getResultStatusColor(result.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Details anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleViewResult(result)}
                            disabled={result.status === 'pending'}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Previous Runs */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Vorherige Läufe
          </Typography>

          {loading && <CircularProgress size={24} />}

          {!loading && runs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Noch keine Benchmarks durchgeführt.
            </Typography>
          )}

          {!loading && runs.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Modelle</TableCell>
                    <TableCell align="center">Tests</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow key={run.id} hover>
                      <TableCell>#{run.id}</TableCell>
                      <TableCell>{formatDateTime(run.created_at)}</TableCell>
                      <TableCell>
                        {run.models.map(m => (
                          <Chip key={m} label={m} size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell align="center">
                        {run.completed_tests}/{run.total_tests}
                        {run.failed_tests > 0 && (
                          <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
                            ({run.failed_tests} fehlgeschlagen)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(run.status)}
                          color={getStatusColor(run.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ergebnisse anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleViewRun(run)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRun(run.id)}
                            disabled={run.status === 'running' || run.status === 'pending'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <GroundingBenchmarkDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        result={selectedResult}
      />
    </Box>
  );
};

export default GroundingBenchmarkTab;
